/* ENSSA Reporting — class-level subtest distribution table.
   Replaces the retired dot plot. Two columns: subtest (name) |
   distribution (a TaskFamilyDistributionBar with per-band click
   drill-down popup listing students in that band). */

import React from "react";
import { Flag, X } from "lucide-react";
import { TaskFamilyDistributionBar } from "@/components/charts/Charts";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { taskBandLabel } from "@/lib/copy";
import type { TaskFamilyBand, TaskFamilyDistribution } from "@/types";

interface DrillState {
  taskLabel: string;
  band: TaskFamilyBand;
  students: { id: string; name: string }[];
}

interface ClassTaskFamilyTableProps {
  distributions: Record<string, TaskFamilyDistribution>;
  focusTaskIds?: readonly string[];
  recommendationHref?: string | null;
  onTeachingFocusOpen?: (taskId: string) => void;
  onOpenStudent?: (id: string) => void;
}

export function ClassTaskFamilyTable({
  distributions,
  focusTaskIds = [],
  recommendationHref = null,
  onTeachingFocusOpen,
  onOpenStudent,
}: ClassTaskFamilyTableProps) {
  const rows = Object.values(distributions);
  const hasStudents = rows.some((d) => d.total > 0);
  const focusTaskIdSet = React.useMemo(() => new Set(focusTaskIds), [focusTaskIds]);
  const [drill, setDrill] = React.useState<DrillState | null>(null);

  // Close on Escape
  React.useEffect(() => {
    if (!drill) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setDrill(null); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [drill]);

  return (
    <TooltipProvider delayDuration={80}>
      <div className="table-scroll" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
          <thead style={{ background: "var(--ink-50)" }}>
            <tr>
              {["Subtest", ""].map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "12px 14px",
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "var(--fg-3)",
                    fontWeight: 700,
                    borderBottom: "1px solid var(--border-subtle)",
                    width: i === 0 ? "30%" : "70%",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!hasStudents ? (
              <tr>
                <td
                  colSpan={2}
                  style={{
                    padding: "28px 14px",
                    textAlign: "center",
                    fontSize: 14,
                    color: "var(--fg-3)",
                    fontStyle: "italic",
                  }}
                >
                  No students match the current filters.
                </td>
              </tr>
            ) : (
              rows.map((dist) => {
                const isTeachingFocus = focusTaskIdSet.has(dist.id);
                return (
                <tr key={dist.id} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                  <td style={{ padding: "16px 14px", verticalAlign: "top", whiteSpace: "normal" }}>
                    <div className="class-task-label-wrap" style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", fontWeight: 700, fontSize: 14, color: "var(--fg-1)" }}>
                      <span>{dist.label}</span>
                      {isTeachingFocus && recommendationHref && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a
                              href={recommendationHref}
                              target="_blank"
                              rel="noreferrer"
                              aria-label={`Open teaching resources for ${dist.label}`}
                              onClick={() => onTeachingFocusOpen?.(dist.id)}
                              className="teaching-focus-flag"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                color: "var(--risk-emerging)",
                                textDecoration: "none",
                              }}
                            >
                              <Flag size={15} strokeWidth={2} aria-hidden="true" />
                            </a>
                          </TooltipTrigger>
                          <TooltipContent side="top" sideOffset={8} className="px-4 py-3 text-sm">
                            <div style={{ lineHeight: 1.45, fontWeight: 900 }}>
                              View teaching resources
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "16px 14px", verticalAlign: "top" }}>
                    <TaskFamilyDistributionBar
                      veryLow={dist.veryLow}
                      low={dist.low}
                      onTrack={dist.onTrack}
                      limited={dist.limited}
                      notAttempted={dist.notAttempted}
                      members={dist.members}
                      onBandClick={(band, students) =>
                        setDrill({ taskLabel: dist.label, band, students })
                      }
                      showCounts
                    />
                  </td>
                </tr>
              );})
            )}
          </tbody>
        </table>
        {focusTaskIdSet.size > 0 && (
          <div className="class-focus-key" style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, color: "var(--fg-2)", fontSize: 12 }}>
            <Flag size={14} strokeWidth={2} color="var(--risk-emerging)" aria-hidden="true" />
            <strong style={{ color: "var(--risk-emerging-fg)" }}>Class focus</strong>
            <span>50% or more of classified students are Low or Very Low</span>
          </div>
        )}
      </div>

      {/* ── Band drill popup ── */}
      {drill && (
        <div
          onClick={() => setDrill(null)}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 50,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--ink-0)",
              borderRadius: 12,
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
              maxWidth: "min(440px, calc(100vw - 32px))",
              width: "100%",
              maxHeight: "80vh",
              overflow: "auto",
            }}
          >
            {/* Header */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "flex-start",
              padding: "16px 20px 12px",
              borderBottom: "1px solid var(--border-subtle)",
              position: "sticky", top: 0, background: "var(--ink-0)", zIndex: 1,
            }}>
              <div>
                <div style={{
                  fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.08em", color: "var(--fg-3)", marginBottom: 4,
                }}>
                  {drill.taskLabel}
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
                  {taskBandLabel(drill.band)}
                </h3>
                <div style={{
                  fontSize: 12, color: "var(--fg-3)", marginTop: 4,
                  fontFamily: "var(--font-mono)",
                }}>
                  {drill.students.length} student{drill.students.length !== 1 ? "s" : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDrill(null)}
                aria-label="Close"
                style={{
                  background: "var(--ink-100)", border: "1px solid var(--border-default)",
                  borderRadius: 6, width: 44, height: 44, cursor: "pointer",
                  color: "var(--fg-2)", display: "flex", alignItems: "center",
                  justifyContent: "center", flexShrink: 0,
                }}
              >
                <X aria-hidden size={18} />
              </button>
            </div>

            {/* Student list */}
            <div style={{ padding: "8px 0" }}>
              {drill.students.length === 0 ? (
                <p style={{ padding: "16px 20px", color: "var(--fg-3)", fontSize: 14, margin: 0 }}>
                  No students in this band.
                </p>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <tbody>
                    {drill.students
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((s) => (
                        <tr key={s.id} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                          <td style={{ padding: "11px 20px", fontWeight: 600 }}>
                            {onOpenStudent ? (
                              <button
                                type="button"
                                onClick={() => { setDrill(null); onOpenStudent(s.id); }}
                                style={{
                                  background: "none", border: 0, padding: 0,
                                  font: "inherit", fontWeight: 600,
                                  color: "var(--link)", cursor: "pointer",
                                  textAlign: "left",
                                  textDecoration: "underline",
                                  textUnderlineOffset: 2,
                                }}
                              >
                                {s.name}
                              </button>
                            ) : (
                              s.name
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </TooltipProvider>
  );
}
